import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import type { PinItemType } from '~/modules/moodboards/type';
import { isProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';

/**
 * Props interface for the CategoryPageRecentlyViewed component
 * @interface CategoryPageRecentlyViewedProps
 * @property {DisplayMode} displayMode - Current display mode (mobile/desktop)
 * @property {TileableModel[]} items - Array of recently viewed items to display
 */
interface CategoryPageRecentlyViewedProps {
    displayMode: DisplayMode;
    items: TileableModel[];
}

/**
 * Component that renders a horizontal scrollable carousel of recently viewed items
 * Shows items in a grid with different sizes for desktop/mobile
 * @param {CategoryPageRecentlyViewedProps} props - Component props
 * @returns {JSX.Element | null} The rendered carousel section or null if no items
 */
export const CategoryPageRecentlyViewed = (
    props: CategoryPageRecentlyViewedProps,
) => {
    const { displayMode, items } = props;
    const isDesktop = displayMode === DisplayMode.Desktop;
    const { isInDesignBoard } = useHandleFollow();

    const isMobile = !isDesktop;
    const recentlyViewedTiles: TileViewModel<TileableModel>[] = items.map(
        (item) => ({
            aspectRatio: isProductTileViewModel(item) ? 'square' : 'default',
            model: item,
            section: 'CategoryPageRecentlyViewed',
            compact: isMobile,
            isFollowed: isInDesignBoard(item.id, getPinType(item)),
            overlay: (
                <TileOverlayWithFollowSelector
                    pinId={item.id}
                    pinType={getPinType(item)}
                    compact={isMobile}
                />
            ),
        }),
    );

    if (recentlyViewedTiles.length === 0) {
        return null;
    }

    return (
        <CarouselSection
            title="Recently viewed"
            size={isDesktop ? 'recentlyViewed' : 'small'}
            dragFree
        >
            {recentlyViewedTiles.map((tile) => (
                <Tile
                    key={`recentlyViewedTiles-${tile.model.id}`}
                    tile={tile}
                />
            ))}
        </CarouselSection>
    );
};

const getPinType = (model: TileableModel): PinItemType => {
    if (isProfessionalTileViewModel(model)) {
        return 'ProfessionalPin';
    }

    if (isProductTileViewModel(model)) {
        return 'ProductPin';
    }

    if (isProjectTileViewModel(model)) {
        return 'ProjectPin';
    }

    if (isArticleTileViewModel(model)) {
        return 'ArticlePin';
    }

    return 'ProductPin';
};
